import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { axios } from '../../context/AuthContext';
import toast from 'react-hot-toast';

const TL = (v) => new Intl.NumberFormat('tr-TR', {
  style: 'currency',
  currency: 'TRY',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
}).format(Number(v) || 0);

const fmtTarih = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('tr-TR');
};

const fmtYuzde = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return '%0';
  return `%${n.toLocaleString('tr-TR', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
};

const today = () => new Date().toISOString().slice(0, 10);
const yearStart = () => `${new Date().getFullYear()}-01-01`;

const BASE_COLUMNS = [
  { key: 'hesap_adi', label: 'Hesap Adı' },
  { key: 'masraf_yeri', label: 'Masraf Yeri' },
  { key: 'planlanan', label: 'Planlanan', type: 'money', align: 'right' },
  { key: 'gerceklesen', label: 'Gerçekleşen', type: 'money', align: 'right' },
  { key: 'fark', label: 'Fark', type: 'money', align: 'right' },
  { key: 'gerceklesme_yuzdesi', label: 'Gerçekleşme %', type: 'percent', align: 'right' },
];

const DETAIL_COLUMNS = {
  gelir: [
    { key: 'vade_tarihi', label: 'Vade Tarihi', type: 'date' },
    { key: 'ogrenci', label: 'Öğrenci' },
    { key: 'egitim_yili', label: 'Eğitim Yılı' },
    { key: 'paket', label: 'Paket' },
    { key: 'odeme_tipi', label: 'Ödeme Tipi' },
    { key: 'taksit_no', label: 'Taksit No' },
  ],
  gider: [
    { key: 'plan_tarihi', label: 'Plan Tarihi', type: 'date' },
    { key: 'aciklama', label: 'Açıklama' },
    { key: 'tedarikci', label: 'Tedarikçi' },
    { key: 'plan_tipi', label: 'Plan Tipi' },
    { key: 'kaynak', label: 'Kaynak' },
  ],
};

function valueOf(row, column) {
  const value = row?.[column.key];
  if (column.type === 'money') return TL(value);
  if (column.type === 'percent') return fmtYuzde(value);
  if (column.type === 'date') return fmtTarih(value);
  return value == null || value === '' ? '—' : value;
}

function getFilename(headers, fallback) {
  const disposition = headers?.['content-disposition'] || headers?.['Content-Disposition'];
  if (!disposition) return fallback;
  const utfMatch = disposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utfMatch?.[1]) return decodeURIComponent(utfMatch[1].replace(/"/g, ''));
  const match = disposition.match(/filename="?([^"]+)"?/i);
  return match?.[1] || fallback;
}

function SummaryCard({ label, value, color, icon, isPercent }) {
  return (
    <div className="stat-card" style={{ '--stat-color': color }}>
      <div className="stat-icon">{icon}</div>
      <div className="stat-value" style={{ fontSize: '1.65rem' }}>{isPercent ? fmtYuzde(value) : TL(value)}</div>
      <div className="stat-label">{label}</div>
    </div>
  );
}

function MobileReportCard({ row, columns, accent }) {
  const mainTitle = valueOf(row, columns.find((column) => column.key === 'hesap_adi') || columns[0]);
  const masrafYeri = valueOf(row, columns.find((column) => column.key === 'masraf_yeri') || {});
  const planlanan = valueOf(row, columns.find((column) => column.key === 'planlanan') || {});
  const gerceklesen = valueOf(row, columns.find((column) => column.key === 'gerceklesen') || {});
  const fark = valueOf(row, columns.find((column) => column.key === 'fark') || {});
  const yuzde = valueOf(row, columns.find((column) => column.key === 'gerceklesme_yuzdesi') || {});
  const detailColumns = columns.filter((column) => !['hesap_adi', 'masraf_yeri', 'planlanan', 'gerceklesen', 'fark', 'gerceklesme_yuzdesi'].includes(column.key));

  return (
    <div style={{
      border: '1px solid var(--border)',
      borderRadius: 16,
      padding: 14,
      background: '#fff',
      boxShadow: '0 2px 10px rgba(15,30,61,0.05)',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'flex-start', marginBottom: 10 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 900, color: 'var(--text-primary)', lineHeight: 1.25, overflowWrap: 'anywhere' }}>{mainTitle}</div>
          <div style={{ marginTop: 4, fontSize: '0.76rem', color: 'var(--text-muted)' }}>{masrafYeri}</div>
        </div>
        <span style={{
          flexShrink: 0,
          padding: '4px 8px',
          borderRadius: 999,
          background: `${accent}14`,
          color: accent,
          fontWeight: 900,
          fontSize: '0.78rem',
        }}>
          {yuzde}
        </span>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: detailColumns.length ? 10 : 0 }}>
        {[
          ['Planlanan', planlanan, '#2563EB'],
          ['Gerçekleşen', gerceklesen, '#10B981'],
          ['Fark', fark, '#E8334A'],
          ['Gerçekleşme', yuzde, accent],
        ].map(([label, value, color]) => (
          <div key={label} style={{ padding: '10px 12px', borderRadius: 12, background: 'var(--bg-elevated)', border: '1px solid var(--border)' }}>
            <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)', fontWeight: 800, textTransform: 'uppercase' }}>{label}</div>
            <div style={{ marginTop: 3, fontWeight: 900, color, fontSize: '0.9rem', overflowWrap: 'anywhere' }}>{value}</div>
          </div>
        ))}
      </div>

      {detailColumns.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 7, paddingTop: 10, borderTop: '1px solid var(--border)' }}>
          {detailColumns.map((column) => (
            <div key={column.key} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: '0.8rem' }}>
              <span style={{ color: 'var(--text-muted)', fontWeight: 800 }}>{column.label}</span>
              <span style={{ color: 'var(--text-secondary)', fontWeight: 700, textAlign: 'right', overflowWrap: 'anywhere' }}>{valueOf(row, column)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function PlanlananRaporPage({ type }) {
  const config = type === 'gelir'
    ? {
        title: 'Planlanan Gelir Listesi',
        subtitle: 'Planlanan tahsilatların gerçekleşme durumunu izleyin',
        icon: '📈',
        endpoint: '/muhasebe/rapor/planlanan-gelir',
        excelEndpoint: '/muhasebe/rapor/planlanan-gelir/excel',
        fallbackFile: 'planlanan_gelir.xlsx',
        accent: '#10B981',
      }
    : {
        title: 'Planlanan Gider Listesi',
        subtitle: 'Planlanan giderlerin gerçekleşme durumunu izleyin',
        icon: '📉',
        endpoint: '/muhasebe/rapor/planlanan-gider',
        excelEndpoint: '/muhasebe/rapor/planlanan-gider/excel',
        fallbackFile: 'planlanan_gider.xlsx',
        accent: '#E8334A',
      };

  const [filtre, setFiltre] = useState({
    liste_tipi: 'ozet',
    tarih_bas: yearStart(),
    tarih_bit: today(),
    ozet_liste: true,
    masraf_yeri: '',
  });
  const [data, setData] = useState([]);
  const [ozet, setOzet] = useState({
    planlanan: 0,
    gerceklesen: 0,
    fark: 0,
    kayit_sayisi: 0,
    gerceklesme_yuzdesi: 0,
  });
  const [loading, setLoading] = useState(false);
  const [excelLoading, setExcelLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 900);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 900);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const params = useMemo(() => {
    const p = new URLSearchParams();
    if (filtre.tarih_bas) p.append('tarih_bas', filtre.tarih_bas);
    if (filtre.tarih_bit) p.append('tarih_bit', filtre.tarih_bit);
    p.append('ozet_liste', filtre.ozet_liste ? 'true' : 'false');
    if (filtre.masraf_yeri.trim()) p.append('masraf_yeri', filtre.masraf_yeri.trim());
    return p;
  }, [filtre]);

  const columns = useMemo(() => {
    const details = DETAIL_COLUMNS[type].filter((column) =>
      !filtre.ozet_liste && data.some((row) => row?.[column.key] != null && row?.[column.key] !== '')
    );
    return [...BASE_COLUMNS, ...details];
  }, [data, filtre.ozet_liste, type]);

  const setOzetListe = (checked) => {
    setFiltre((prev) => ({
      ...prev,
      ozet_liste: checked,
      liste_tipi: checked ? 'ozet' : 'detay',
    }));
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${config.endpoint}?${params.toString()}`);
      if (res.data.success) {
        setData(Array.isArray(res.data.data) ? res.data.data : []);
        setOzet(res.data.ozet || {});
        setHasLoaded(true);
      } else {
        toast.error(res.data.message || 'Rapor oluşturulamadı');
      }
    } catch (e) {
      toast.error(e.response?.data?.message || 'Rapor yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, [config.endpoint, params]);

  const handleExcel = async () => {
    setExcelLoading(true);
    try {
      const res = await axios.get(`${config.excelEndpoint}?${params.toString()}`, { responseType: 'blob' });
      const blobUrl = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = getFilename(res.headers, config.fallbackFile);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
      toast.success('Excel dosyası indirildi');
    } catch (e) {
      toast.error(e.response?.data?.message || 'Excel indirilemedi');
    } finally {
      setExcelLoading(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div className="section-header">
        <div>
          <h2 style={{ fontWeight: 900 }}>{config.icon} {config.title}</h2>
          <div className="section-subtitle">{config.subtitle}</div>
        </div>
        <div className="muh-filter-row">
          <button className="btn btn-outline" onClick={handleExcel} disabled={excelLoading}>
            {excelLoading ? <div className="spinner" style={{ width: 16, height: 16 }} /> : '📥 Excel/Çıktı Al'}
          </button>
          <button className="btn btn-primary" onClick={load} disabled={loading} style={{ background: config.accent, borderColor: config.accent }}>
            {loading ? <div className="spinner" style={{ width: 16, height: 16 }} /> : 'Rapor Oluştur'}
          </button>
        </div>
      </div>

      <div className="grid grid-4" style={{ gap: isMobile ? 10 : 18 }}>
        <SummaryCard label="Toplam Planlanan" value={ozet.planlanan} color="#2563EB" icon="▦" />
        <SummaryCard label="Toplam Gerçekleşen" value={ozet.gerceklesen} color="#10B981" icon="✓" />
        <SummaryCard label="Fark" value={ozet.fark} color={Number(ozet.fark) < 0 ? '#E8334A' : '#F59E0B'} icon="△" />
        <SummaryCard label="Gerçekleşme %" value={ozet.gerceklesme_yuzdesi} color="#7C3AED" icon="%" isPercent />
      </div>

      <div className="card" style={{ padding: '14px 16px' }}>
        <div className="muh-filter-row" style={{ alignItems: 'center' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label className="form-label">Liste Tipi</label>
            <select
              className="form-input"
              style={{ width: 180 }}
              value={filtre.liste_tipi}
              onChange={(e) => setOzetListe(e.target.value === 'ozet')}
            >
              <option value="ozet">Özet Liste</option>
              <option value="detay">Detay Liste</option>
            </select>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label className="form-label">İşlem Tarihi Başlangıç</label>
            <input className="form-input" type="date" style={{ width: 170 }} value={filtre.tarih_bas}
              onChange={(e) => setFiltre((f) => ({ ...f, tarih_bas: e.target.value }))} />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label className="form-label">İşlem Tarihi Bitiş</label>
            <input className="form-input" type="date" style={{ width: 170 }} value={filtre.tarih_bit}
              onChange={(e) => setFiltre((f) => ({ ...f, tarih_bit: e.target.value }))} />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label className="form-label">Masraf Yeri</label>
            <input className="form-input" style={{ width: 190 }} placeholder="Tümü" value={filtre.masraf_yeri}
              onChange={(e) => setFiltre((f) => ({ ...f, masraf_yeri: e.target.value }))} />
          </div>

          <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 22, fontSize: '0.82rem', fontWeight: 800, color: 'var(--text-secondary)', cursor: 'pointer' }}>
            <input type="checkbox" checked={filtre.ozet_liste} onChange={(e) => setOzetListe(e.target.checked)} style={{ width: 18, height: 18 }} />
            Özet Liste
          </label>

          <button className="btn btn-ghost btn-sm" style={{ marginTop: 20 }} onClick={() => setFiltre({ liste_tipi: 'ozet', tarih_bas: yearStart(), tarih_bit: today(), ozet_liste: true, masraf_yeri: '' })}>
            ✕ Temizle
          </button>
        </div>
      </div>

      <div className="card" style={{ padding: 0 }}>
        {loading ? (
          <div className="page-loader" style={{ minHeight: 240 }}><div className="spinner" /></div>
        ) : data.length === 0 ? (
          <div className="empty-state" style={{ padding: 48 }}>
            <div style={{ fontSize: '2.2rem', marginBottom: 8 }}>{hasLoaded ? '∅' : config.icon}</div>
            <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>
              {hasLoaded ? 'Bu kriterlerle kayıt bulunamadı.' : 'Filtreleri seçip rapor oluşturun.'}
            </p>
          </div>
        ) : isMobile ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: 12 }}>
            {data.map((row, index) => (
              <MobileReportCard
                key={row.id || `${row.hesap_adi || 'row'}-${index}`}
                row={row}
                columns={columns}
                accent={config.accent}
              />
            ))}
          </div>
        ) : (
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  {columns.map((column) => (
                    <th key={column.key} style={{ textAlign: column.align || 'left' }}>{column.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.map((row, index) => (
                  <tr key={row.id || `${row.hesap_adi || 'row'}-${index}`}>
                    {columns.map((column) => (
                      <td key={column.key} data-label={column.label} style={{ textAlign: column.align || 'left', fontWeight: column.type === 'money' || column.type === 'percent' ? 800 : undefined }}>
                        {valueOf(row, column)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export function PlanlananGelirPage() {
  return <PlanlananRaporPage type="gelir" />;
}

export function PlanlananGiderPage() {
  return <PlanlananRaporPage type="gider" />;
}
